import * as React from "react";
import { useEffect, useState, useRef, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, Typography, IconButton, Tooltip, Slider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CropIcon from "@mui/icons-material/Crop";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import * as mime from "mime";
import useDrive from "../../hooks/useDrive";
import useNotification from "../../hooks/useNotification";
import FileService from "../../services/FileService";
import DriveService from "../../services/DriveService";
import FileBlobCache from "../../services/FileBlobCache";
import ThreeDViewer from "../ThreeDViewer";
import styles from "./file-view-modal.module.css";

const IS_3D    = ext => ["stl", "obj", "ply"].includes(ext);
const IS_IMAGE = ext => ["png", "jpg", "jpeg", "gif", "webp", "ico"].includes(ext);

/* ── Resize if > 5MB or > 4K resolution before any processing ── */
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_DIM   = 3840;             // 4K width

function resizeIfNeeded(img) {
  const { naturalWidth: w, naturalHeight: h } = img;
  const needsResize = w > MAX_DIM || h > MAX_DIM;

  let dw = w, dh = h;
  if (needsResize) {
    const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
    dw = Math.round(w * ratio);
    dh = Math.round(h * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width  = dw;
  canvas.height = dh;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, dw, dh);
  return { canvas, ctx, resized: needsResize };
}

/* ── Canvas helpers — pixel manipulation, no ctx.filter (unreliable) ── */
function applyAdjustments(src, brightness, contrast, saturation) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Resize if > 4K
      const { canvas, ctx } = resizeIfNeeded(img);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;

      for (let i = 0; i < d.length; i += 4) {
        let r = d[i], g = d[i+1], b = d[i+2];
        r *= brightness; g *= brightness; b *= brightness;
        r = (r - 128) * contrast + 128;
        g = (g - 128) * contrast + 128;
        b = (b - 128) * contrast + 128;
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        r = lum + saturation * (r - lum);
        g = lum + saturation * (g - lum);
        b = lum + saturation * (b - lum);
        d[i]   = Math.max(0, Math.min(255, r));
        d[i+1] = Math.max(0, Math.min(255, g));
        d[i+2] = Math.max(0, Math.min(255, b));
      }

      ctx.putImageData(imageData, 0, 0);

      // Output quality: lower if original was > 5MB
      const quality = 0.92;
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = src;
  });
}

function cropImageData(src, rect, naturalW, naturalH, displayW, displayH) {
  const scaleX = naturalW / displayW;
  const scaleY = naturalH / displayH;
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      // Compute crop in natural pixels
      const cx = Math.round(rect.x * scaleX);
      const cy = Math.round(rect.y * scaleY);
      const cw = Math.round(rect.w * scaleX);
      const ch = Math.round(rect.h * scaleY);

      // Limit output to 4K
      const ratio = Math.min(1, MAX_DIM / cw, MAX_DIM / ch);
      const ow = Math.round(cw * ratio);
      const oh = Math.round(ch * ratio);

      const canvas = document.createElement("canvas");
      canvas.width  = ow;
      canvas.height = oh;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, cx, cy, cw, ch, 0, 0, ow, oh);
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    img.src = src;
  });
}

/* ── Crop overlay ── */
function CropOverlay({ onConfirm, onCancel }) {
  const [crop, setCrop] = useState({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
  const dragging = useRef(null);
  const dragStart = useRef({ mx: 0, my: 0, crop: { x: 0, y: 0, w: 0, h: 0 } });

  const handleMouseDown = (e, handle) => {
    e.stopPropagation(); e.preventDefault();
    dragging.current = handle;
    dragStart.current = { mx: e.clientX, my: e.clientY, crop: { ...crop } };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const el = document.getElementById("crop-overlay-ref");
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = (e.clientX - dragStart.current.mx) / rect.width;
    const dy = (e.clientY - dragStart.current.my) / rect.height;
    const s = dragStart.current.crop;
    let { x, y, w, h } = s;
    switch (dragging.current) {
      case "move": x = Math.max(0, Math.min(1 - w, s.x + dx)); y = Math.max(0, Math.min(1 - h, s.y + dy)); break;
      case "br":   w = Math.max(0.05, Math.min(1 - s.x, s.w + dx)); h = Math.max(0.05, Math.min(1 - s.y, s.h + dy)); break;
      case "tl":   x = Math.max(0, Math.min(s.x + s.w - 0.05, s.x + dx)); y = Math.max(0, Math.min(s.y + s.h - 0.05, s.y + dy)); w = s.w - (x - s.x); h = s.h - (y - s.y); break;
      case "tr":   w = Math.max(0.05, Math.min(1 - s.x, s.w + dx)); y = Math.max(0, Math.min(s.y + s.h - 0.05, s.y + dy)); h = s.h - (y - s.y); break;
      case "bl":   x = Math.max(0, Math.min(s.x + s.w - 0.05, s.x + dx)); w = s.w - (x - s.x); h = Math.max(0.05, Math.min(1 - s.y, s.h + dy)); break;
    }
    setCrop({ x, y, w, h });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragging.current = null;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const bx = `${crop.x * 100}%`, by = `${crop.y * 100}%`;
  const bw = `${crop.w * 100}%`, bh = `${crop.h * 100}%`;

  const hSx = { position: "absolute", width: 12, height: 12, bgcolor: "#fff", border: "2px solid #7c6af7", borderRadius: "50%", zIndex: 4, transform: "translate(-50%,-50%)" };

  return (
    <Box id="crop-overlay-ref" sx={{ position: "absolute", inset: 0, zIndex: 10, cursor: "crosshair" }}>
      {/* Dimmed areas */}
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: by, bgcolor: "rgba(0,0,0,0.55)" }} />
      <Box sx={{ position: "absolute", top: by, left: 0, width: bx, height: bh, bgcolor: "rgba(0,0,0,0.55)" }} />
      <Box sx={{ position: "absolute", top: by, left: `calc(${bx} + ${bw})`, right: 0, height: bh, bgcolor: "rgba(0,0,0,0.55)" }} />
      <Box sx={{ position: "absolute", top: `calc(${by} + ${bh})`, left: 0, right: 0, bottom: 0, bgcolor: "rgba(0,0,0,0.55)" }} />
      {/* Crop box */}
      <Box onMouseDown={e => handleMouseDown(e, "move")} sx={{ position: "absolute", left: bx, top: by, width: bw, height: bh, border: "2px solid #7c6af7", zIndex: 3, cursor: "move",
        backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.1) 1px,transparent 1px)", backgroundSize: "33.3% 33.3%" }} />
      {/* Handles */}
      <Box onMouseDown={e => handleMouseDown(e, "tl")} sx={{ ...hSx, left: bx, top: by, cursor: "nwse-resize" }} />
      <Box onMouseDown={e => handleMouseDown(e, "tr")} sx={{ ...hSx, left: `calc(${bx} + ${bw})`, top: by, cursor: "nesw-resize" }} />
      <Box onMouseDown={e => handleMouseDown(e, "bl")} sx={{ ...hSx, left: bx, top: `calc(${by} + ${bh})`, cursor: "nesw-resize" }} />
      <Box onMouseDown={e => handleMouseDown(e, "br")} sx={{ ...hSx, left: `calc(${bx} + ${bw})`, top: `calc(${by} + ${bh})`, cursor: "nwse-resize" }} />
      {/* Confirm/cancel */}
      <Box sx={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 1, zIndex: 5 }}>
        <IconButton onClick={() => onConfirm(crop)} sx={{ bgcolor: "#7c6af7", color: "#fff", "&:hover": { bgcolor: "#6a5acd" }, width: 36, height: 36 }}>
          <CheckIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton onClick={onCancel} sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }, width: 36, height: 36 }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

/* ── Main component ── */
export default function FileViewModal() {
  const { CurrentDrive, previewObject, changePreviewObject, changeCurrentDrive } = useDrive();
  const { addNotification } = useNotification();
  const [downloadURL, setDownloadURL] = useState(null);
  const [currentSrc, setCurrentSrc]   = useState(null);
  const [history, setHistory]         = useState([]);
  const [tool, setTool]               = useState(null);
  const [saving, setSaving]           = useState(false);
  const imgRef = useRef(null);

  // Adjust sliders
  const [brightness, setBrightness] = useState(1);
  const [contrast,   setContrast]   = useState(1);
  const [saturation, setSaturation] = useState(1);

  useEffect(() => {
    setDownloadURL(null); setCurrentSrc(null); setHistory([]); setTool(null);
    resetSliders();
    if (previewObject?.type === "file") fetchUrl();
  }, [previewObject]);

  const fetchUrl = async () => {
    const { parent, driveType } = CurrentDrive;
    const filePath = parent ? `${parent}/${previewObject.Key}` : previewObject.Key;
    const res = await FileService.getDownLoadLink(parent, previewObject.Key, driveType);
    if (!res) return;
    const url = res.url || res;
    setDownloadURL(url);

    // Use cache — avoids re-downloading if file was already opened this session
    try {
      const blobUrl = await FileBlobCache.get(url, filePath);
      // Auto-resize if > 5MB or > 4K
      const fetchedBlob = await fetch(blobUrl).then(r => r.blob());
      if (fetchedBlob.size > MAX_BYTES || IS_IMAGE(ext)) {
        const optimized = await new Promise(resolve => {
          const img = new Image();
          img.onload = () => {
            if (img.naturalWidth <= MAX_DIM && img.naturalHeight <= MAX_DIM && fetchedBlob.size <= MAX_BYTES) {
              resolve(blobUrl); return; // no resize needed
            }
            const { canvas } = resizeIfNeeded(img);
            resolve(canvas.toDataURL("image/jpeg", 0.92));
          };
          img.onerror = () => resolve(blobUrl);
          img.src = blobUrl;
        });
        setCurrentSrc(optimized);
      } else {
        setCurrentSrc(blobUrl);
      }
    } catch {
      setCurrentSrc(url);
    }
  };

  const resetSliders = () => { setBrightness(1); setContrast(1); setSaturation(1); };

  const pushHistory = (src) => { setHistory(h => [...h, currentSrc]); setCurrentSrc(src); };

  const handleUndo = () => {
    if (!history.length) return;
    setCurrentSrc(history[history.length - 1]);
    setHistory(h => h.slice(0, -1));
  };

  const handleCropConfirm = async (cropNorm) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = { x: cropNorm.x * img.clientWidth, y: cropNorm.y * img.clientHeight, w: cropNorm.w * img.clientWidth, h: cropNorm.h * img.clientHeight };
    const result = await cropImageData(currentSrc, rect, img.naturalWidth, img.naturalHeight, img.clientWidth, img.clientHeight);
    pushHistory(result);
    setTool(null);
  };

  const handleApplyAdjust = async () => {
    if (!currentSrc) return;
    try {
      const result = await applyAdjustments(currentSrc, brightness, contrast, saturation);
      pushHistory(result);
    } catch (e) {
      console.error("Image processing failed:", e);
    }
    setTool(null);
    resetSliders();
  };

  const handleDownload = () => {
    const name = previewObject?.Key || "file";
    // Inside the SmileFy webapp (iframe): hand the file back to the parent so it
    // lands in the patient documentation, instead of a browser download. Send a
    // CROSS-ORIGIN-readable source — the edited image as a data: URL, else the S3
    // download URL; never a blob: URL (those are origin-scoped, the parent can't
    // read them). The payload matches swebapp ConnectionsPopup's 'drive-file-selected'.
    if (window.parent && window.parent !== window) {
      const result = (history.length > 0 && currentSrc && currentSrc.startsWith("data:"))
        ? currentSrc
        : downloadURL;
      if (!result) return;
      window.parent.postMessage(
        { type: "drive-file-selected", data: { data: { result } }, fileName: name },
        "*"
      );
      return;
    }
    const src = currentSrc || downloadURL;
    if (!src) return;
    const a = document.createElement("a");
    a.href = src; a.download = name; a.target = "_blank"; a.click();
  };

  const handleSave = async () => {
    if (!currentSrc || history.length === 0) return;
    setSaving(true);
    try {
      const { parent, driveType } = CurrentDrive;
      const fileName = previewObject.Key;
      const folder = parent === "" ? "/" : parent;

      // Convert current edited src (blob URL or dataURL) to blob
      const fetchRes = await fetch(currentSrc);
      const blob = await fetchRes.blob();

      // Step 1: checkFile — same as FileUpload does (registers upload with backend)
      const checkRes = await FileService.checkFile({ file_name: fileName, size: blob.size, folder }, driveType);

      // Step 2: get presigned S3 URL (works whether file exists or not after checkFile)
      const bucketUrl = await DriveService.getS3BucketUrl(
        { file_name: checkRes?.file_name || fileName, size: blob.size, folder },
        driveType
      );

      if (!bucketUrl) throw new Error("No upload URL returned");

      // Step 3: upload blob directly to S3 (no auth headers — same as FileUpload)
      const contentType = mime.getType(fileName) || blob.type || "image/jpeg";
      await axios({ method: "PUT", url: bucketUrl, headers: { "Content-Type": contentType }, data: blob });

      // Step 4: refresh drive listing
      const drive = await DriveService.getFileTree(parent, driveType);
      changeCurrentDrive({ parent, drive, driveType });

      // Invalidate cache so next open gets the updated file
      const filePath = parent ? `${parent}/${fileName}` : fileName;
      FileBlobCache.invalidate(filePath);

      addNotification({ type: "success", message: "Image saved successfully" });
      setHistory([]);
    } catch (e) {
      console.error("Save failed:", e);
      addNotification({ type: "error", message: "Save failed — " + (e.message || "") });
    } finally {
      setSaving(false);
    }
  };

  const close = () => changePreviewObject(null);

  if (!previewObject) return null;

  const ext  = previewObject.Key?.split(".").pop()?.toLowerCase() || "";
  const name = previewObject.Key || "";
  const isImg = IS_IMAGE(ext);

  /* ── 3D viewer ── */
  if (previewObject.type === "file" && IS_3D(ext)) {
    return (
      <Dialog open fullScreen onClose={close} PaperProps={{ className: styles.fullScreenPaper }}>
        {downloadURL
          ? <ThreeDViewer url={downloadURL} filename={name} onClose={close} />
          : <div className={styles.loadingWrap}><Typography sx={{ color: "#8892b0", fontSize: 14 }}>Preparing model…</Typography></div>
        }
        {/* Download to SmileFy — also for 3D models (STL/OBJ/PLY). handleDownload
            sends the S3 downloadURL (no edits on 3D) to the parent webapp. */}
        {downloadURL && (
          <Box onClick={handleDownload} sx={{
            position: "fixed", bottom: 24, right: 24, zIndex: 10,
            display: "flex", alignItems: "center", gap: 1,
            px: 2.5, py: 1.25, borderRadius: 2, cursor: "pointer",
            bgcolor: "#e85878", color: "#fff", boxShadow: "0 6px 24px rgba(232,88,120,0.4)",
            transition: "all 0.15s", "&:hover": { bgcolor: "#c43058" },
          }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Download to SmileFy</Typography>
          </Box>
        )}
      </Dialog>
    );
  }

  const btnSx = (active) => ({
    display: "flex", alignItems: "center", gap: 0.75,
    px: 1.75, py: 0.75, borderRadius: 2, cursor: "pointer",
    border: `1px solid ${active ? "#7c6af7" : "var(--border)"}`,
    bgcolor: active ? "rgba(124,106,247,0.18)" : "var(--bg-input)",
    color: active ? "#a78bfa" : "var(--text-2)",
    transition: "all 0.15s",
    "&:hover": { bgcolor: active ? "rgba(124,106,247,0.25)" : "var(--border)", color: "var(--text-1)" },
  });

  return (
    <Dialog open={!!previewObject} onClose={close} fullWidth={false} maxWidth={false}
      PaperProps={{ sx: {
        bgcolor: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: 3,
        backgroundImage: "none", overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
        maxWidth: "95vw", maxHeight: "95vh", width: "auto",
      }}}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 1.5, bgcolor: "var(--bg-surface)", borderBottom: "1px solid var(--border)" }}>
        <Typography sx={{ flex: 1, textAlign: "center", fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>{name}</Typography>
        <IconButton size="small" onClick={close} sx={{ color: "var(--text-3)", "&:hover": { color: "var(--text-1)" }, ml: 1 }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Image */}
      <Box sx={{ bgcolor: "var(--bg-base)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
        {currentSrc && isImg ? (
          <>
            <img ref={imgRef} src={currentSrc} alt="preview" style={{
              display: "block", maxWidth: "88vw", maxHeight: "72vh",
              width: "auto", height: "auto", objectFit: "contain", imageRendering: "high-quality",
              filter: tool === "adjust"
                ? `brightness(${brightness}) contrast(${contrast}) saturate(${saturation})`
                : "none",
              willChange: tool === "adjust" ? "filter" : "auto",
              transition: "filter 0.1s",
            }} />
            {tool === "crop" && <CropOverlay onConfirm={handleCropConfirm} onCancel={() => setTool(null)} />}
          </>
        ) : currentSrc ? (
          <Typography sx={{ color: "var(--text-3)", fontSize: 14, p: 6 }}>No preview for .{ext} files</Typography>
        ) : (
          <Typography sx={{ color: "var(--text-3)", fontSize: 14, p: 6 }}>Loading…</Typography>
        )}
      </Box>

      {/* Adjust sliders */}
      {tool === "adjust" && isImg && (
        <Box sx={{ bgcolor: "var(--bg-surface)", borderTop: "1px solid var(--border)", px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          {[
            { label: "Brightness", value: brightness, set: setBrightness, min: 0.3, max: 2, step: 0.05 },
            { label: "Contrast",   value: contrast,   set: setContrast,   min: 0.3, max: 2, step: 0.05 },
            { label: "Saturation", value: saturation, set: setSaturation, min: 0,   max: 3, step: 0.05 },
          ].map(({ label, value, set, min, max, step }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 200 }}>
              <Typography sx={{ color: "var(--text-3)", fontSize: 12, fontWeight: 600, minWidth: 72 }}>{label}</Typography>
              <Slider size="small" min={min} max={max} step={step} value={value}
                onChange={(_, v) => set(v)}
                sx={{ flex: 1, color: "#7c6af7", "& .MuiSlider-thumb": { width: 12, height: 12 } }}
              />
              <Typography sx={{ color: "#7c6af7", fontSize: 11, minWidth: 36, textAlign: "right" }}>{Math.round(value * 100)}%</Typography>
            </Box>
          ))}
          <IconButton onClick={handleApplyAdjust} sx={{ bgcolor: "#7c6af7", color: "#fff", borderRadius: 2, "&:hover": { bgcolor: "#6a5acd" }, width: 36, height: 36 }}>
            <CheckIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 1.5, bgcolor: "var(--bg-surface)", borderTop: "1px solid var(--border)", gap: 2, flexWrap: "wrap" }}>

        {/* Edit tools — only for images */}
        {isImg && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={btnSx(tool === "crop")} onClick={() => setTool(tool === "crop" ? null : "crop")}>
              <CropIcon sx={{ fontSize: 17 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Crop</Typography>
            </Box>
            <Box sx={btnSx(tool === "adjust")} onClick={() => setTool(tool === "adjust" ? null : "adjust")}>
              <AutoFixHighIcon sx={{ fontSize: 17 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Auto Correct</Typography>
            </Box>
            {history.length > 0 && (
              <Tooltip title="Undo" placement="top">
                <IconButton onClick={handleUndo} size="small"
                  sx={{ color: "var(--text-3)", bgcolor: "var(--bg-input)", border: "1px solid var(--border)", borderRadius: 2, "&:hover": { color: "var(--text-1)" } }}>
                  <UndoIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Download */}
        <Box sx={{ display: "flex", gap: 1.5, ml: "auto" }}>
          {/* Save — only shown when edits exist */}
          {history.length > 0 && (
            <Box onClick={!saving ? handleSave : undefined} sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 2.5, py: 1, borderRadius: 2, cursor: saving ? "default" : "pointer",
              bgcolor: "#e85878", color: "#fff", transition: "all 0.15s",
              opacity: saving ? 0.7 : 1,
              "&:hover": !saving ? { bgcolor: "#c43058" } : {},
            }}>
              {saving
                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                : <SaveOutlinedIcon sx={{ fontSize: 20 }} />
              }
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {saving ? "Saving…" : "Save"}
              </Typography>
            </Box>
          )}

          <Box onClick={handleDownload} sx={{
            display: "flex", alignItems: "center", gap: 1,
            px: 2.5, py: 1, borderRadius: 2, cursor: "pointer",
            bgcolor: "var(--bg-input)", border: "1px solid var(--border)",
            color: "var(--text-2)", transition: "all 0.15s",
            "&:hover": { bgcolor: "var(--border)", color: "var(--text-1)", borderColor: "var(--border-h)" },
          }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Download to SmileFy</Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
